'use client'

import { useEffect, useRef, useState } from 'react'
import { Mail } from 'lucide-react'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import { gmailWebUrl, planGmailOpen } from '@/lib/auth/openGmail'

// How long iOS gets to switch to the Gmail app before we offer the web inbox.
const IOS_APP_WAIT_MS = 2000

/**
 * Shown when /api/auth/otp reported a Google-hosted mailbox: one tap to the
 * inbox. Android and computers get a new tab (see openGmail.ts for why not
 * the Android app), so this page and its code box stay put.
 *
 * iOS never navigates this tab away: Safari's "Open in Gmail?" question keeps
 * the page visible while it waits, and a missing app or an in-app browser
 * just does nothing. So when the app has not taken over, we offer the web
 * inbox as a link the person taps (a new tab, the code box stays).
 */
export default function OpenGmailButton({ email }: { email: string }) {
  const { t } = useLanguage()
  const [appMissed, setAppMissed] = useState(false)
  const pending = useRef<() => void>(undefined)
  useEffect(() => () => pending.current?.(), [])

  const open = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const plan = planGmailOpen(navigator.userAgent, navigator.maxTouchPoints ?? 0, email)
    if (plan.kind === 'tab') return // the link's own new-tab behaviour
    e.preventDefault()
    pending.current?.()
    setAppMissed(false)
    // The app took over if the page went to the background.
    const timer = window.setTimeout(() => {
      if (document.visibilityState === 'visible') setAppMissed(true)
    }, IOS_APP_WAIT_MS)
    const onHide = () => {
      if (document.visibilityState === 'hidden') window.clearTimeout(timer)
    }
    document.addEventListener('visibilitychange', onHide)
    pending.current = () => {
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onHide)
    }
    window.location.href = plan.appUrl
  }

  return (
    <div className="space-y-2">
      <a
        href={gmailWebUrl(email)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={open}
        className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-gray-200 bg-white px-4 py-3 font-semibold text-gray-800 hover:bg-gray-50"
      >
        <Mail className="h-5 w-5 text-red-500" aria-hidden="true" />
        {t('authModal.openGmail')}
      </a>
      {appMissed && (
        <p className="text-sm text-gray-600" role="status">
          {t('authModal.gmailAppNotOpened')}{' '}
          <a href={gmailWebUrl(email)} target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 underline underline-offset-2">
            {t('authModal.openGmailInBrowser')}
          </a>
        </p>
      )}
    </div>
  )
}
